import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { G2S } from 'core/g2s';
import * as i18next from 'i18next';
import router from 'src/router';
import * as api from 'core/api';
import * as payment from 'core/payment';
import * as utils from 'core/utils';
import { SbpModalComponent } from 'src/components/payment/sbp-modal';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

export interface OrderBookingProductComponentModel {
    state: string;
    error: string;
    title: string;
    reservation: api.ReservationInfo | null;
}

@WithRender
@Component({
    components: {
        'sbp-modal': SbpModalComponent
    }
})
export default class OrderBookingProductComponent extends Vue {
    $refs!: {
        sbpModal: SbpModalComponent;
    }
    
    @Prop()
    orderGuid: string;

    @Prop()
    productId: string;

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    gaAlreadySubmitted: string[] = [];

    model: OrderBookingProductComponentModel = {
        state: 'loading',
        error: '',
        title: '',
        reservation: null
    }

    products: api.Product[] = [];

    paymentModel: payment.PaymentModel | null = null;
    selectedProductId: number = 0;

    cancelProductRequestsInProgress = {};

    redirectUrl: string | null = null;

    get amountToPay() {
        if (this.model.reservation) {
            let price = 0;

            this.products.forEach(p => {
                if (p.priceType === 'PerPassenger') {
                    price += this.model.reservation!.passengers.length * p.price;
                } else {
                    price += p.price;
                }
            });

            if (this.paymentModel && this.paymentModel.selectedPaymentType.commission) {
                price += this.paymentModel.selectedPaymentType.commission;
            }

            return i18next.t('label_amount_to_pay', <any>{ i: (<any>this.$options.filters).price(price, false, this.products[0] ? this.products[0].currencyCode : '') });
        }
        return i18next.t('label_amount_to_pay', <any>{ i: 0 });
    }

    get selectedProduct(): api.Product {
        return utils.find(this.products, (item: api.Product) => {
            return item.id === this.selectedProductId;
        });
    }

    mounted() {
        if (this.$route.query.ct) {
            utils.Cookies.setChoiceType(parseInt(<string>this.$route.query.ct));
        }

        this.loadModel();
    }

    loadModel() {
        this.model.state = 'loading';
        this.model.title = i18next.t('title_order_booking_product');
        window.document.title = this.model.title;
        const ct = parseInt(<string>this.$route.query.ct);
        const redirectDomain = <string>this.$route.query.redirect_domain || undefined;

        const request: api.ProductBookingDetailsRequest = {
            reservationPublicId: this.orderGuid,
            productIds: this.parseIds(this.productId),
            choiceType: ct || utils.Cookies.getChoiceType() || api.ProductChoiceType.notSet,
            redirectDomain
        };

        new api.ReservationService().getProductBookingDetails(request).then(
            (result: api.ProductBookingDetails) => {
                if (result.redirectUrl) {
                    this.redirectUrl = result.redirectUrl;
                }

                if (result.products
                    && result.products.length > 0
                    && result.paymentTypes
                    && result.paymentTypes.length > 0) {
                    this.model.reservation = result.reservationInfo;

                    this.products = result.products;

                    this.paymentModel = new payment.PaymentModel(result.paymentTypes);

                    this.model.state = 'form';
                } else {
                    if (G2S.config.theme === 'aviasales') {
                        (<any>this.$root.$refs).fullScreenDialog.show({
                            title: i18next.t('error'),
                            text: i18next.t('error_payment_not_available'),
                            firstBtnText: 'Перейти к заказу',
                            firstBtnFn: () => {
                                if (this.redirectUrl) {
                                    location.href = this.redirectUrl;
                                } else {
                                    (<any>this.$root.$refs).fullScreenDialog.hide();
                                    const reservationPublicId = result.reservationInfo.parentReservationPublicId ?? result.reservationInfo.reservationPublicId;
                                    router.push('/order/' + reservationPublicId);
                                }
                            }
                        });
                    } else {
                        (<any>this.$root.$refs).messageDialog.show({
                            message: i18next.t('error_payment_not_available'),
                            ok: i18next.t('button_ok'),
                            onOkCallback: () => {
                                router.push('/order/' + result.reservationInfo.reservationPublicId);
                            }
                        });
                    }
                }
            },
            (error) => {
                this.model.error = i18next.t('error_server_tryagain');

                if (error.response) {
                    if (error.response.status === 404) {
                        this.model.error = i18next.t('error_order_notfound');
                    } else {
                        if (error?.response?.data?.localizedMessage) {
                            if (G2S.config.theme === 'aviasales') {
                                if (error.response.data.redirectUrl) {
                                    (<any>this.$root.$refs).fullScreenDialog.show({
                                        title: i18next.t('error'),
                                        text: error.response.data.localizedMessage,
                                        firstBtnText: 'Перейти к заказу',
                                        firstBtnFn: () => {
                                            location.href = error.response.data.redirectUrl;
                                        }
                                    });
                                } else {
                                    (<any>this.$root.$refs).fullScreenDialog.show({
                                        title: i18next.t('error'),
                                        text: error.response.data.localizedMessage,
                                        firstBtnText: 'На главную',
                                        firstBtnFn: () => {
                                            location.href = 'https://www.aviasales.ru/';
                                        }
                                    });
                                }
                            } else {
                                (<any>this.$root.$refs).messageDialog.show({
                                    message: error.response.data.localizedMessage,
                                    ok: i18next.t('button_ok'),
                                    onOkCallback: () => {
                                        location.href = '/';
                                    }
                                });
                            }

                            return;
                        }
                    }
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }

                this.model.state = 'error';
            }
        );
    }

    @Watch('$route')
    onRouteChanged() {
        this.loadModel();
    }

    onProductDetails(productId: number) {
        this.selectedProductId = productId;

        (<any>this.$refs).modalProduct.show();
    }

    onSubmit() {
        const error = (<any>this.$refs).paymentForm.validate();

        if (error) {
            (<any>this.$root.$refs).messageDialog.show({
                message: error
            });
        } else {
            this.model.state = 'processing';

            const ct = parseInt(<string>this.$route.query.ct);
            const redirectDomain = <string>this.$route.query.redirect_domain || undefined;

            const request: api.BookProductsRequest = {
                productIds: [],
                parentReservationPublicId: this.model.reservation!.reservationPublicId,
                paymentTypeId: this.paymentModel!.selectedPaymentType.paymentTypeId,
                confirmationPageUrlTemplate: G2S.config.desktopUrl + '/go/order/payment/_reservationPublicId_/_billPublicId_',
                bonusAmount: 0,
                gaClientID: (<any>window).__gaClientID,
                clientDevice: (<any>this).$getClientDevice(),
                choiceType: ct || utils.Cookies.getChoiceType() || api.ProductChoiceType.notSet,
                redirectDomain
            };

            this.products.forEach(p => {
                request.productIds.push(p.id);
            });

            if (this.paymentModel!.selectedPaymentType.kind === 'CreditCardInplace') {
                request.creditCard = this.paymentModel!.getCardWithEncryptedPacket() || undefined;

                if (!request.creditCard) {
                    (<any>this.$root.$refs).messageDialog.show({
                        title: i18next.t('error'),
                        message: i18next.t('error_payment_card_not_available')
                    });

                    this.model.state = 'form';

                    (<any>this).$ga.sendEvent(
                        'flights.booking',
                        `payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}.notavailable`.toLowerCase(),
                        `flights.booking.payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}.notavailable`.toLowerCase(),
                        this.gaParams
                    );

                    return null;
                }
            }

            new api.ReservationService().bookProducts(request).then(
                (result: api.BookProductsResponse) => {
                    if (result.paymentInfo) {
                        if (result.paymentInfo?.inPlace.status === 'SbpQr' && result.paymentInfo.redirect) {
                            this.$refs.sbpModal.show(result.paymentInfo.redirect.url);

                            const pollPaymentStatusRequest: api.FinishPreauth3dsRequest = {
                                reservationPublicId: result.reservationPublicId,
                                billPublicId: result.paymentInfo.billPublicId,
                                paymentId: result.paymentInfo.paymentId
                            };

                            this.pollPaymentStatus(pollPaymentStatusRequest);
                        } else if (result.paymentInfo.redirect) {
                            // Payment requires redirecting to external url
                            utils.Http.redirect(result.paymentInfo.redirect);
                        }
                        else if (result.paymentInfo.inPlace) {
                            if (result.paymentInfo.inPlace.succeed) {
                                this.handleSuccess(result.parentReservationPublicId);
                            } else {
                                this.handleFail(result.paymentInfo.inPlace);
                            }
                        } else {
                            console.error('Payment: strange result');

                            router.push('/order/' + result.parentReservationPublicId);
                        }
                    } else {
                        router.push('/order/' + result.parentReservationPublicId);
                    }
                },
                (error) => {
                    let message = i18next.t('error_server_tryagain');

                    if (error.response) {
                        console.log('api/BookProducts XHR failed', error.response.status, error.response.data);

                        if (error.response.data
                            && error.response.data.localizedMessage) {
                            message = error.response.data.localizedMessage;
                        }
                    } else {
                        console.log('api/BookProducts XHR error', error.message);
                    }

                    (<any>this).$ga.sendPageview('/order/booking/product/error', 'Order / Booking / Product / Error', this.gaParams);

                    this.model.state = 'form';

                    if (G2S.config.theme === 'aviasales') {
                        (<any>this.$root.$refs).fullScreenDialog.show({
                            title: i18next.t('error_payment'),
                            text: message,
                            firstBtnText: 'Попробовать ещё раз',
                            firstBtnFn: () => {
                                (<any>this.$root.$refs).fullScreenDialog.hide();
                            },
                            secondBtnText: 'Вернуться к заказу',
                            secondBtnFn: () => {
                                if (this.redirectUrl) {
                                    location.href = this.redirectUrl;
                                } else {
                                    (<any>this.$root.$refs).fullScreenDialog.hide();
                                    const reservationPublicId = this.model.reservation!.parentReservationPublicId ?? this.model.reservation!.reservationPublicId;
                                    router.push('/order/' + reservationPublicId);
                                }
                            }
                        });
                    } else {
                        (<any>this.$root.$refs).messageDialog.show({
                            title: i18next.t('error'),
                            message: message
                        });
                    }
                }
            );
        }
    }

    redirectToOrderPage() {
        // Force href to remove any query params for feng shui
        window.location.href = '/order/' + this.orderGuid;
    }

    parseIds(ids: string) {
        return ids.replace(/[^\d_]/g, '').split('_').map(id => parseInt(id)).filter(id => !!id);
    }

    handleSuccess(parentReservationPublicId: string) {
        // Wait 5 seconds to give time to server to issue tickets and change status.
        window.setTimeout(() => {
            (<any>this).$ga.sendPageview('/flights/booking/payment/thankyou', 'Flights / Booking / Payment / Thankyou');

            if (G2S.config.theme === 'aviasales') {
                (<any>this.$root.$refs).fullScreenDialog.show({
                    title: i18next.t('title_thank_you'),
                    text: i18next.t('label_payment_success_reservation'),
                    firstBtnText: 'Перейти к заказу',
                    firstBtnFn: () => {
                        if (this.redirectUrl) {
                            location.href = this.redirectUrl;
                        } else {
                            (<any>this.$root.$refs).fullScreenDialog.hide();
                            router.push('/order/' + parentReservationPublicId);
                        }
                    }
                });
            } else {
                (<any>this.$root.$refs).messageDialog.show({
                    title: i18next.t('title_thank_you'),
                    message: i18next.t('label_payment_success_reservation'),
                    ok: i18next.t('button_ok'),
                    onOkCallback: () => {
                        router.push('/order/' + parentReservationPublicId);
                    }
                });
            }
        }, 5000);
    }

    handleFail(paymentInfoInPlace: api.PaymentInfoInPlace) {
        (<any>this).$ga.sendEvent(
            'flights.booking',
            `error.payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}`.toLowerCase(),
            `flights.booking.error.payment.${this.paymentModel!.selectedPaymentType.kind}.${this.paymentModel!.selectedPaymentType.paymentTypeId}`.toLowerCase()
        );
        
        if (paymentInfoInPlace.exceptionMessage) {
            console.error('Payment error: ' + paymentInfoInPlace.exceptionMessage);
        }

        this.model.state = 'form';

        if (G2S.config.theme === 'aviasales') {
            (<any>this.$root.$refs).fullScreenDialog.show({
                title: i18next.t('error_payment'),
                text: paymentInfoInPlace.errorMessage,
                firstBtnText: 'Попробовать ещё раз',
                firstBtnFn: () => {
                    (<any>this.$root.$refs).fullScreenDialog.hide();
                },
                secondBtnText: 'Вернуться к заказу',
                secondBtnFn: () => {
                    if (this.redirectUrl) {
                        location.href = this.redirectUrl;
                    } else {
                        (<any>this.$root.$refs).fullScreenDialog.hide();
                        const reservationPublicId = this.model.reservation!.parentReservationPublicId ?? this.model.reservation!.reservationPublicId;
                        router.push('/order/' + reservationPublicId);
                    }
                }
            });
        } else {
            (<any>this.$root.$refs).messageDialog.show({
                title: i18next.t('error_payment'),
                message: paymentInfoInPlace.errorMessage
            });
        }
    }

    async pollPaymentStatus(request: api.FinishPreauth3dsRequest) {
        const reservationService = new api.ReservationService();

        const pollResult = await reservationService.pollPaymentQrStatus(request);

        if (pollResult.paymentInfo?.redirect?.url) {
            location.href = pollResult.paymentInfo.redirect.url;
        }

        if (pollResult.paymentInfo?.inPlace?.status === 'Error') {
            this.$refs.sbpModal.close();
            this.handleFail(pollResult.paymentInfo?.inPlace);
        }

        if (pollResult.paymentInfo?.inPlace?.status === 'Success') {
            const parentReservationPublicId = this.model.reservation!.reservationPublicId;
            this.handleSuccess(parentReservationPublicId);
        }
    }

    onSbpModalClose() {
        this.model.state = 'form';
        api.ReservationService.needToStopSbpPolling = true;
    }
}
